import { avatarSrc } from "@/lib/defaults";
import { pausableInterval } from "@/lib/appVisibility";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, getToken } from "@/lib/api";
import { resolveMediaUrl } from "@/components/FileUpload";
import { useApp } from "@/context/AppContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { X, Minus, ArrowLeft, ChevronDown, Search, Ban, MoreVertical } from "lucide-react";
import { BlockUserDialog } from "@/components/BlockUserDialog";
import { toast } from "sonner";
import { useIsMobile } from "@/lib/useIsMobile";
import { useBackHandler } from "@/lib/backButton";
import { localeFor } from "@/lib/dateLocale";
import { playMessagePing } from "@/lib/notifySound";
import { hapticLight } from "@/lib/haptics";
import { clearDeliveredChatNotifications } from "@/lib/chatNotifClear";
import { ReportDialog } from "@/components/ReportDialog";
import { ChatMedia } from "@/components/chat/ChatMedia";
import { MessageList } from "@/components/chat/MessageList";
import { sameMessage } from "@/components/chat/sameMessage";
import { Composer } from "@/components/chat/Composer";
import { SelectionToolbar } from "@/components/chat/SelectionToolbar";
import { ChatLightbox } from "@/components/chat/ChatLightbox";
import { ForwardSheet } from "@/components/chat/ForwardSheet";
import { ConvActionsSheet } from "@/components/chat/ConvActionsSheet";
import { StarredSheet } from "@/components/chat/StarredSheet";
import { ReactionsSheet } from "@/components/chat/ReactionsSheet";
import { useDockOverModal, dockLayerStyle } from "@/components/chat/dockLayer";

// Re-exported for the Messages page + Admin audit view (kept for backwards-compat import path).
export { ChatMedia };

// Copy text with a graceful fallback for WebViews/browsers that block the async clipboard API.
const copyToClipboard = (text) => {
  try {
    if (navigator.clipboard?.writeText) { navigator.clipboard.writeText(text).catch(() => {}); return; }
  } catch {}
  try {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.focus(); ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  } catch {}
};
const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");

// Facebook-style floating private chat window (bottom-right) / full-screen on mobile.
export const ChatDock = () => {
  const { user, chatTarget, closeChat, openChat, t, lang, config } = useApp();
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [minimized, setMinimized] = useState(false);
  const [peerTyping, setPeerTyping] = useState(false);
  const [peerLastRead, setPeerLastRead] = useState(null);
  const [peerLastDelivered, setPeerLastDelivered] = useState(null);
  const [presence, setPresence] = useState(null);
  const [reactTarget, setReactTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // multi-select (bulk delete/forward)
  const [reportMsg, setReportMsg] = useState(null);
  const [reporting, setReporting] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { items, index, messageId }
  const [confirmDelete, setConfirmDelete] = useState(null); // message(s) pending delete confirmation
  const [forwardMsgs, setForwardMsgs] = useState([]); // message(s) being forwarded ("Envoyer à")
  const [convFlags, setConvFlags] = useState(null); // drapeaux de la conversation pour moi (demande de message…)
  const [menuOpen, setMenuOpen] = useState(false);   // menu ⋮ (feuille d'actions de la conversation)
  const [starredOpen, setStarredOpen] = useState(false); // ⭐ messages importants
  const [reactionsForId, setReactionsForId] = useState(null); // lot 729 — feuille « Qui a réagi » (id du message)
  const pendingJumpRef = useRef(null); // message ⭐ d'une AUTRE conversation à centrer après ouverture
  const bottomRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimer = useRef(null);
  const chatTargetRef = useRef(null);
  chatTargetRef.current = chatTarget;
  const convIdRef = useRef(null);
  convIdRef.current = convId;
  const composerRef = useRef(null);
  // Outbox: pending outgoing messages (still uploading / failed) mirrored OUTSIDE the
  // per-conversation `messages` state so they survive closing & reopening the chat
  // (ChatDock stays mounted for the app's lifetime). id -> { recipientId, convId, msg }.
  const outboxRef = useRef(new Map());
  const outbox = useRef({
    add: (msg) => { const e = outboxRef.current.get(msg.id) || {}; outboxRef.current.set(msg.id, { ...e, recipientId: chatTargetRef.current?.id, convId: convIdRef.current, msg }); },
    patch: (id, p) => { const e = outboxRef.current.get(id); if (e) e.msg = { ...e.msg, ...p }; },
    remove: (id) => { outboxRef.current.delete(id); },
    setJob: (id, job) => { const e = outboxRef.current.get(id); if (e) e.job = job; },
    getJob: (id) => outboxRef.current.get(id)?.job,
  }).current;
  // Instant chat open (WhatsApp-style): remember the conversation id per recipient and the
  // last loaded messages per conversation IN MEMORY (never on disk, for privacy). Reopening
  // a chat we already viewed shows instantly + refreshes in the background, and skips the
  // extra /chat/conversations round-trip.
  const msgCacheRef = useRef(new Map());   // convId -> last server messages
  const convCacheRef = useRef(new Map());  // recipientId -> convId

  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const oldestCursorRef = useRef(null);   // created_at of the oldest message currently shown
  const loadingOlderRef = useRef(false);
  const [hasMoreNewer, setHasMoreNewer] = useState(false);  // true while browsing history (jumped)
  const newestCursorRef = useRef(null);   // created_at of the newest message currently shown
  const loadingNewerRef = useRef(false);
  const viewingHistoryRef = useRef(false); // mirror of hasMoreNewer for the poll/scroll closures
  const jumpTargetRef = useRef(null);      // message id to centre + flash after a jump
  const suppressAutoLoadRef = useRef(0);   // ignore auto older/newer paging until this ts (post-jump settle)
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimerRef = useRef(null);

  const loadMessages = useCallback(async (cid) => {
    try {
      const { data } = await api.get(`/chat/conversations/${cid}/messages`);
      const raw = data.messages || [];
      msgCacheRef.current.set(cid, raw);  // in-memory cache for instant reopen
      let finalList = null;
      // Carry over any local (blob) previews we already hold for these messages so a
      // refresh doesn't drop them before the remote media is cached (avoids flicker).
      setMessages((prev) => {
        // Lot 728 — un message inchangé garde son objet (bulle mémoïsée non re-rendue) ; les aperçus locaux (blob)
        // déjà tenus sont reportés pour qu'un rafraîchissement ne les perde pas avant la mise en cache du média distant.
        const prevById = new Map(prev.map((x) => [x.id, x]));
        const server = raw.map((sm) => {
          const p = prevById.get(sm.id);
          if (p && !p.pending && !p.failed && sameMessage(p, sm)) return p;
          return p && (p._localPreview || p._localGroup) ? { ...sm, _localPreview: p._localPreview, _localGroup: p._localGroup } : sm;
        });
        // Keep optimistic bubbles still uploading/sending (or failed) — a slow video
        // upload isn't on the server yet, so a refresh must not drop its pending bubble.
        const serverIds = new Set(server.map((s) => s.id));
        // …but drop a FAILED optimistic bubble that was actually delivered server-side
        // (a native background/network blip lost the POST response): if a server message
        // from me with the same text + media kind exists within ~5 min, it's the same
        // send → remove the ghost so no bogus "Réessayer" lingers under a video that sent.
        const hasMedia = (mm) => !!(mm.media_url || (mm.media_group && mm.media_group.length) || mm._localGroup);
        const deliveredTwin = (o) => server.some((s) =>
          s.sender_id === o.sender_id &&
          (s.content || "") === (o.content || "") &&
          hasMedia(s) === hasMedia(o) &&
          Math.abs(new Date(s.created_at).getTime() - new Date(o.created_at).getTime()) < 5 * 60 * 1000);
        const pending = prev.filter((x) => (x.pending || x.failed) && !serverIds.has(x.id) && !(x.failed && hasMedia(x) && deliveredTwin(x)));
        const merged = pending.length ? [...server, ...pending] : [...server];
        // Re-attach still-pending outgoing media held in the outbox for THIS chat, so a slow
        // upload that outlived a chat close/reopen reappears (WhatsApp-style).
        const have = new Set(merged.map((x) => x.id));
        for (const e of outboxRef.current.values()) {
          if (have.has(e.msg.id) || serverIds.has(e.msg.id)) continue;
          if (e.convId === cid || e.recipientId === chatTargetRef.current?.id) merged.push(e.msg);
        }
        // Preserve OLDER history already fetched via "load older" pagination (it sits
        // before this recent window) so a background refresh/poll never collapses it.
        const oldestNew = server.length ? server[0].created_at : null;
        const keepOlder = oldestNew ? prev.filter((x) => !x.pending && !x.failed && !serverIds.has(x.id) && x.created_at < oldestNew) : [];
        const result = [...keepOlder, ...merged];
        finalList = result;
        return result;
      });
      oldestCursorRef.current = finalList && finalList.length ? finalList[0].created_at : (raw.length ? raw[0].created_at : null);
      // More history exists if the server says so, OR we already have older pages loaded.
      setHasMoreOlder((prev) => !!data.has_more || prev);
      // A recent-window load means we're back at the live end of the conversation.
      newestCursorRef.current = finalList && finalList.length ? finalList[finalList.length - 1].created_at : (raw.length ? raw[raw.length - 1].created_at : null);
      setHasMoreNewer(false);
      const lr = data.conversation?.last_read || {};
      const peers = Object.entries(lr).filter(([uid]) => uid !== user.id).map(([, v]) => v).filter(Boolean).sort();
      setPeerLastRead(peers.length ? peers[peers.length - 1] : null);
      const ld = data.conversation?.last_delivered || {};
      const deliveredPeers = Object.entries(ld).filter(([uid]) => uid !== user.id).map(([, v]) => v).filter(Boolean).sort();
      setPeerLastDelivered(deliveredPeers.length ? deliveredPeers[deliveredPeers.length - 1] : null);
      setConvFlags(data.conversation?.flags || null);
      window.dispatchEvent(new CustomEvent("yalola:read"));
      clearDeliveredChatNotifications(cid); // lue → la notification empilée quitte la barre du téléphone / navigateur
    } catch { /* noop */ }
  }, [user]);

  // Infinite scroll UP: load the previous page of OLDER messages before the current
  // top, prepend them, and keep the scroll position anchored so the view doesn't jump.
  const loadOlderMessages = useCallback(async () => {
    const cid = convIdRef.current;
    if (!cid || loadingOlderRef.current || !oldestCursorRef.current) return;
    loadingOlderRef.current = true;
    const el = scrollContRef.current;
    const prevH = el ? el.scrollHeight : 0;
    try {
      const { data } = await api.get(`/chat/conversations/${cid}/messages`, { params: { before: oldestCursorRef.current, limit: 40 } });
      const older = data.messages || [];
      if (older.length) {
        setMessages((prev) => {
          const have = new Set(prev.map((x) => x.id));
          const dedup = older.filter((m) => !have.has(m.id));
          return dedup.length ? [...dedup, ...prev] : prev;
        });
        oldestCursorRef.current = older[0].created_at;
        requestAnimationFrame(() => { const e = scrollContRef.current; if (e) e.scrollTop = e.scrollHeight - prevH; });
      }
      setHasMoreOlder(!!data.has_more);
    } catch { /* noop */ }
    finally { loadingOlderRef.current = false; }
  }, []);

  // Infinite scroll DOWN after a jump: pull the NEXT page of newer messages and append.
  const loadNewerMessages = useCallback(async () => {
    const cid = convIdRef.current;
    if (!cid || loadingNewerRef.current || !newestCursorRef.current || !viewingHistoryRef.current) return;
    loadingNewerRef.current = true;
    try {
      const { data } = await api.get(`/chat/conversations/${cid}/messages`, { params: { after: newestCursorRef.current, limit: 40 } });
      const newer = data.messages || [];
      if (newer.length) {
        setMessages((prev) => {
          const have = new Set(prev.map((x) => x.id));
          const dedup = newer.filter((m) => !have.has(m.id));
          return dedup.length ? [...prev, ...dedup] : prev;
        });
        newestCursorRef.current = newer[newer.length - 1].created_at;
      }
      setHasMoreNewer(!!data.has_more_newer);
    } catch { /* noop */ }
    finally { loadingNewerRef.current = false; }
  }, []);

  // Jump to (and highlight) a specific — possibly very old — message: load a window
  // centred on it; the smart-scroll effect then centres + flashes it.
  const jumpToMessage = useCallback(async (msg) => {
    const cid = convIdRef.current;
    if (!cid || !msg?.created_at) return;
    try {
      const { data } = await api.get(`/chat/conversations/${cid}/messages`, { params: { around: msg.created_at, limit: 30 } });
      const win = data.messages || [];
      if (!win.length) return;
      jumpTargetRef.current = msg.id;
      suppressAutoLoadRef.current = Date.now() + 1000;  // let the centre-scroll settle first
      msgCacheRef.current.set(cid, win);
      setMessages(win);
      oldestCursorRef.current = win[0].created_at;
      newestCursorRef.current = win[win.length - 1].created_at;
      setHasMoreOlder(!!data.has_more);
      setHasMoreNewer(!!data.has_more_newer);
      setSearchOpen(false); setSearchResults([]); setSearchQ("");
    } catch { /* noop */ }
  }, []);

  // Back to the live end of the conversation (after browsing history via a jump).
  const returnToLatest = useCallback(() => {
    const cid = convIdRef.current;
    if (!cid) return;
    jumpTargetRef.current = null;
    setHasMoreNewer(false);
    scrolledConvRef.current = null;   // re-trigger the "snap to bottom" on next render
    loadMessages(cid);
  }, [loadMessages]);

  // Debounced in-conversation message search.
  const runSearch = useCallback((q) => {
    setSearchQ(q);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const query = (q || "").trim();
    if (query.length < 2) { setSearchResults([]); setSearching(false); return; }
    setSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      const cid = convIdRef.current;
      if (!cid) { setSearching(false); return; }
      try {
        const { data } = await api.get(`/chat/conversations/${cid}/search`, { params: { q: query, limit: 30 } });
        setSearchResults(data.results || []);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
  }, []);

  useEffect(() => { viewingHistoryRef.current = hasMoreNewer; }, [hasMoreNewer]);

  // Message ⭐ choisi dans une AUTRE conversation : une fois ce fil ouvert, on le centre.
  useEffect(() => {
    const pj = pendingJumpRef.current;
    if (!convId || !pj || pj.conversation_id !== convId) return;
    pendingJumpRef.current = null;
    const tmo = setTimeout(() => jumpToMessage(pj), 400);
    return () => clearTimeout(tmo);
  }, [convId, jumpToMessage]);

  useEffect(() => {
    if (!chatTarget || !user) return;
    const cachedConv = convCacheRef.current.get(chatTarget.id) || null;
    const cachedMsgs = cachedConv ? msgCacheRef.current.get(cachedConv) : null;
    // Seed instantly from the in-memory cache + any pending outbox media → no 1-2s blank.
    const seed = cachedMsgs ? [...cachedMsgs] : [];
    for (const e of outboxRef.current.values()) if (e.recipientId === chatTarget.id && !seed.some((s) => s.id === e.msg.id)) seed.push(e.msg);
    setMessages(seed); setConvId(cachedConv); setMinimized(false); setPeerTyping(false); setPeerLastRead(null); setPeerLastDelivered(null); setPresence(null); setReactTarget(null); setSelectedIds(new Set()); setConfirmDelete(null); setLightbox(null); setConvFlags(null); setMenuOpen(false);
    // Reset pagination for the new conversation (cursor is re-seeded by loadMessages).
    setHasMoreOlder(false); oldestCursorRef.current = null; loadingOlderRef.current = false;
    setHasMoreNewer(false); newestCursorRef.current = null; loadingNewerRef.current = false; viewingHistoryRef.current = false; jumpTargetRef.current = null;
    setSearchOpen(false); setSearchQ(""); setSearchResults([]); setSearching(false);
    if (cachedConv) loadMessages(cachedConv);  // refresh in background, no visible reload
    let cancelled = false;
    (async () => {
      try {
        // Resolve the 1:1 conversation DIRECTLY by user id (robust even when the user
        // has more conversations than the list endpoint returns) → old chats always
        // load their history instead of showing an empty "first message" state.
        const { data } = await api.get(`/chat/conversations/with/${chatTarget.id}`);
        if (cancelled || !data.conversation_id) return;
        convCacheRef.current.set(chatTarget.id, data.conversation_id);
        if (data.conversation_id !== cachedConv) { setConvId(data.conversation_id); loadMessages(data.conversation_id); }
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [chatTarget, user, loadMessages]);

  // Global realtime chat socket: one connection, broadcasts messages app-wide.
  // « Instantané comme WhatsApp » (lot 488) : battement de cœur ping/pong (20 s) → une connexion à moitié morte
  // (veille mobile, bascule 4G/Wi-Fi) est détectée en < 45 s et rouverte ; reconnexion immédiate quand l'app
  // revient au premier plan / le réseau revient ; à chaque (re)connexion l'événement yalola:ws {resumed:true}
  // déclenche un rechargement de la conversation ouverte pour combler le trou.
  useEffect(() => {
    if (!user) return undefined;
    let ws; let alive = true; let retry; let hb; let attempt = 0; let lastPong = 0; let everOpened = false;
    const clearHb = () => { if (hb) clearInterval(hb); hb = null; };
    const schedule = () => {
      if (!alive) return;
      clearTimeout(retry);
      const delay = Math.min(15000, 1000 * 2 ** attempt);
      attempt += 1;
      retry = setTimeout(connect, delay);
    };
    const connect = () => {
      if (!alive) return;
      clearTimeout(retry);
      try { ws = new WebSocket(`${WS_BASE}/api/ws/chat?token=${getToken()}`); } catch { schedule(); return; }
      wsRef.current = ws;
      ws.onopen = () => {
        attempt = 0; lastPong = Date.now();
        window.dispatchEvent(new CustomEvent("yalola:ws", { detail: { connected: true, resumed: everOpened } }));
        everOpened = true;
        announceActiveRef.current?.();
        clearHb();
        hb = setInterval(() => {
          if (ws.readyState !== WebSocket.OPEN) return;
          if (Date.now() - lastPong > 45000) { try { ws.close(); } catch { /* noop */ } return; }
          // Le ping réaffirme « je lis cette conversation » (le serveur l'oublie après 50 s sans nouvelle).
          try { ws.send(JSON.stringify({ type: "ping", t: Date.now(), peer: activePeerRef.current?.() ?? null })); } catch { /* noop */ }
        }, 20000);
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          lastPong = Date.now();  // any frame proves the link is alive
          if (msg.type === "pong") return;
          if (msg.type === "chat_message") window.dispatchEvent(new CustomEvent("yalola:chat", { detail: msg }));
          else if (msg.type === "typing") window.dispatchEvent(new CustomEvent("yalola:typing", { detail: msg }));
          else if (msg.type === "read") window.dispatchEvent(new CustomEvent("yalola:read", { detail: msg }));
          else if (msg.type === "delivered") window.dispatchEvent(new CustomEvent("yalola:delivered", { detail: msg }));
          else if (msg.type === "reaction") window.dispatchEvent(new CustomEvent("yalola:reaction", { detail: msg }));
          else if (msg.type === "message_hidden") window.dispatchEvent(new CustomEvent("yalola:msg_hidden", { detail: msg }));
          else if (msg.type === "message_edited") window.dispatchEvent(new CustomEvent("yalola:msg_edited", { detail: msg }));
          else if (msg.type === "message_deleted") window.dispatchEvent(new CustomEvent("yalola:msg_deleted", { detail: msg }));
          else if (msg.type === "conversation_purged") window.dispatchEvent(new CustomEvent("yalola:conv_purged", { detail: msg }));
        } catch { /* noop */ }
      };
      ws.onclose = () => { clearHb(); window.dispatchEvent(new CustomEvent("yalola:ws", { detail: { connected: false } })); schedule(); };
    };
    // Retour au premier plan / réseau de retour : on ne laisse pas courir le délai de reconnexion.
    const wake = () => {
      if (!alive || document.visibilityState === "hidden") { announceActiveRef.current?.(); return; }
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) { attempt = 0; connect(); return; }
      if (ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: "ping", t: Date.now() })); } catch { /* noop */ }
        announceActiveRef.current?.();
        window.dispatchEvent(new CustomEvent("yalola:ws", { detail: { connected: true, resumed: true } }));
      }
    };
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("online", wake);
    window.addEventListener("focus", wake);
    let appSub = null;
    import("@capacitor/app").then(({ App }) => App.addListener("appStateChange", ({ isActive }) => { if (isActive) wake(); })).then((s) => { appSub = s; }).catch(() => {});
    connect();
    return () => {
      alive = false;
      clearTimeout(retry);
      clearHb();
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("online", wake);
      window.removeEventListener("focus", wake);
      try { appSub?.remove(); } catch { /* noop */ }
      const s = ws;
      if (!s) return;
      // Detach onclose so this teardown doesn't schedule a reconnect. If the socket is
      // still CONNECTING (React StrictMode double-mount in dev / fast navigation), closing
      // it now throws the benign "closed before the connection is established" warning —
      // instead defer the close until it has opened, so the handshake completes cleanly.
      s.onclose = null;
      if (s.readyState === WebSocket.CONNECTING) s.onopen = () => { try { s.close(); } catch { /* noop */ } };
      else { try { s.close(); } catch { /* noop */ } }
    };
  }, [user]);

  // « J'ai cette conversation ouverte au premier plan » → le serveur n'envoie ni cloche ni push pour ses
  // messages (je les lis en direct). Ré-annoncé à chaque changement de conversation / réduction / visibilité.
  const announceActiveRef = useRef(null);
  const activePeerRef = useRef(null);
  activePeerRef.current = () => {
    const visible = document.visibilityState !== "hidden";
    return visible && !minimized && chatTargetRef.current?.id ? chatTargetRef.current.id : null;
  };
  announceActiveRef.current = () => {
    const s = wsRef.current;
    if (!s || s.readyState !== WebSocket.OPEN) return;
    try { s.send(JSON.stringify({ type: "active", peer: activePeerRef.current() })); } catch { /* noop */ }
  };
  useEffect(() => { announceActiveRef.current?.(); }, [chatTarget, minimized]);

  // Reconnexion (ou retour au premier plan) → recharge la conversation ouverte pour rattraper les messages
  // arrivés pendant la coupure, sans attendre le rafraîchissement périodique.
  useEffect(() => {
    const onWs = (e) => {
      if (!e.detail?.connected || !e.detail?.resumed) return;
      const cid = convIdRef.current;
      if (cid && !viewingHistoryRef.current) loadMessages(cid);
    };
    window.addEventListener("yalola:ws", onWs);
    return () => window.removeEventListener("yalola:ws", onWs);
  }, [loadMessages]);

  // Instant display of incoming messages for the open conversation.
  useEffect(() => {
    const onMsg = (e) => {
      const { conversation_id, message, sender, is_request } = e.detail || {};
      if (!chatTarget || sender?.id !== chatTarget.id) return;
      setConvId((c) => c || conversation_id);
      setMessages((m) => (m.some((x) => x.id === message.id) ? m : [...m, { ...message, _fresh: true }]));  // lot 728 — entrée animée
      // Un inconnu m'écrit : la conversation devient une demande (bandeau Accepter / Supprimer).
      if (is_request) setConvFlags((f) => ({ ...(f || {}), is_request: true, request_pending: true }));
    };
    // Mon premier message à un inconnu est parti en « demande » : rappel discret sous le fil.
    // Si je réponds à une demande reçue, elle est acceptée automatiquement (le bandeau disparaît).
    const onSent = (e) => {
      const d = e.detail || {};
      if (d.type !== "sent") return;
      if (d.request_pending) setConvFlags((f) => ({ ...(f || {}), request_pending: true, is_request: false }));
      else if (d.conversation_id) setConvFlags((f) => ((f?.request_pending || f?.is_request) ? { ...f, request_pending: false, is_request: false } : f));
    };
    window.addEventListener("yalola:chat", onSent);
    window.addEventListener("yalola:chat", onMsg);
    return () => { window.removeEventListener("yalola:chat", onMsg); window.removeEventListener("yalola:chat", onSent); };
  }, [chatTarget]);

  // Peer "is typing…" (ephemeral, auto-clears after 3s).
  useEffect(() => {
    const onTyping = (e) => {
      if (!chatTarget || e.detail?.from !== chatTarget.id) return;
      setPeerTyping(true);
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => setPeerTyping(false), 3000);
    };
    window.addEventListener("yalola:typing", onTyping);
    return () => { window.removeEventListener("yalola:typing", onTyping); clearTimeout(typingTimer.current); };
  }, [chatTarget]);

  // Read receipts: the peer opened the conversation.
  useEffect(() => {
    const onRead = (e) => {
      const d = e.detail;
      if (!d || !chatTarget) return;
      if (d.reader_id === chatTarget.id && d.at) setPeerLastRead(d.at);
    };
    window.addEventListener("yalola:read", onRead);
    return () => window.removeEventListener("yalola:read", onRead);
  }, [chatTarget]);

  // Accusé « distribué » : l'app du pair vient de recevoir nos messages (double coche grise, WhatsApp).
  useEffect(() => {
    const onDelivered = (e) => {
      const d = e.detail;
      if (!d || !chatTarget) return;
      if (d.to === chatTarget.id && d.at) setPeerLastDelivered((prev) => (!prev || d.at > prev ? d.at : prev));
    };
    window.addEventListener("yalola:delivered", onDelivered);
    return () => window.removeEventListener("yalola:delivered", onDelivered);
  }, [chatTarget]);

  // Live presence (online / last seen) for the peer.
  useEffect(() => {
    if (!chatTarget) return;
    let alive = true;
    const load = () => api.get(`/presence?ids=${chatTarget.id}`)
      .then(({ data }) => { if (alive) setPresence(data.presence?.[chatTarget.id] || null); })
      .catch(() => {});
    load();
    const stop = pausableInterval(load, 20000); // lot 699 — repos en arrière-plan
    return () => { alive = false; stop(); };
  }, [chatTarget]);

  // Reactions from the peer (real-time).
  useEffect(() => {
    const onReact = (e) => {
      const d = e.detail;
      if (!d) return;
      setMessages((ms) => ms.map((m) => (m.id === d.message_id ? { ...m, reactions: d.reactions || {} } : m)));
    };
    window.addEventListener("yalola:reaction", onReact);
    return () => window.removeEventListener("yalola:reaction", onReact);
  }, []);

  // A message was hidden by moderation (CSAE auto-hide) — remove it live for everyone.
  useEffect(() => {
    const onHidden = (e) => {
      const d = e.detail;
      if (!d?.message_id) return;
      setMessages((ms) => ms.filter((m) => m.id !== d.message_id));
    };
    window.addEventListener("yalola:msg_hidden", onHidden);
    return () => window.removeEventListener("yalola:msg_hidden", onHidden);
  }, []);

  // A message was edited by its sender — update it live.
  useEffect(() => {
    const onEdited = (e) => {
      const d = e.detail;
      if (!d?.message_id) return;
      setMessages((ms) => ms.map((m) => (m.id === d.message_id ? { ...m, content: d.content, edited: true } : m)));
    };
    window.addEventListener("yalola:msg_edited", onEdited);
    return () => window.removeEventListener("yalola:msg_edited", onEdited);
  }, []);

  // A message was deleted by its sender — replace it with the "deleted" placeholder live.
  useEffect(() => {
    const onDeleted = (e) => {
      const d = e.detail;
      if (!d?.message_id) return;
      if (d.purged) { setMessages((ms) => ms.filter((m) => m.id !== d.message_id)); return; }
      setMessages((ms) => ms.map((m) => (m.id === d.message_id ? { ...m, is_deleted: true, content: "", media_url: null, media_type: null } : m)));
    };
    window.addEventListener("yalola:msg_deleted", onDeleted);
    return () => window.removeEventListener("yalola:msg_deleted", onDeleted);
  }, []);

  // Conversation supprimée définitivement par un administrateur : fil fermé, boîte rechargée.
  useEffect(() => {
    const onPurged = (e) => {
      const cid = e.detail?.conversation_id;
      if (!cid) return;
      window.dispatchEvent(new CustomEvent("yalola:conv"));
      if (convIdRef.current === cid) { setMessages([]); setConvId(null); closeChat(); }
    };
    window.addEventListener("yalola:conv_purged", onPurged);
    return () => window.removeEventListener("yalola:conv_purged", onPurged);
  }, [closeChat]);

  // Discreet ping + vibration on any incoming message (unless already viewing that chat).
  useEffect(() => {
    const onAny = (e) => {
      const s = e.detail?.sender;
      if (!s || s.id === user.id) return;
      if (chatTargetRef.current?.id === s.id) return;
      playMessagePing();
    };
    window.addEventListener("yalola:chat", onAny);
    return () => window.removeEventListener("yalola:chat", onAny);
  }, [user]);

  useEffect(() => {
    if (!convId || minimized) return;
    const stop = pausableInterval(() => { if (!viewingHistoryRef.current) loadMessages(convId); }, 15000); // lot 699
    return () => stop();
  }, [convId, minimized, loadMessages]);

  // Smart auto-scroll: pin to the latest message only when the user is already at
  // the bottom, so we never interrupt someone reading older messages. A floating
  // "↓ new message" pill appears otherwise.
  const [atBottom, setAtBottom] = useState(true);
  const [newCount, setNewCount] = useState(0);
  const scrollContRef = useRef(null);
  const lastMsgIdRef = useRef(null);
  // Mirror of `atBottom` in a ref so the smart-scroll effect always reads the LIVE
  // value (not a stale closure) when a message arrives via WS or the 15s poll.
  const atBottomRef = useRef(true);
  // While set to a future timestamp, force-stick to the bottom. Covers media that
  // reports its real height a beat AFTER we scrolled (photo/video/audio), which would
  // otherwise leave the freshly sent bubble just below the fold — the user had to
  // scroll down to see it loading.
  const stickRef = useRef(0);

  const onMessagesScroll = () => {
    const el = scrollContRef.current;
    if (!el) return;
    // After a jump we programmatically centre the target → ignore the resulting scroll
    // event(s) so we don't instantly cascade-load every newer message back to the end.
    const suppressed = Date.now() < suppressAutoLoadRef.current;
    // Near the TOP → pull the previous page of older messages (infinite scroll up).
    if (!suppressed && el.scrollTop < 60 && hasMoreOlder && !loadingOlderRef.current) loadOlderMessages();
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (!suppressed && near && hasMoreNewer && !loadingNewerRef.current) loadNewerMessages();
    atBottomRef.current = near;
    setAtBottom(near);
    if (near) setNewCount(0);
  };
  const jumpToBottom = () => {
    if (hasMoreNewer) { returnToLatest(); return; }  // browsing history → back to the live end
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    setNewCount(0);
    atBottomRef.current = true;
    setAtBottom(true);
  };

  // On first open of a conversation, jump instantly to the bottom and re-pin a few
  // times while media load. Afterwards, only auto-scroll for MY messages or when the
  // user is already at the bottom; otherwise surface the "↓ new message" pill.
  const scrolledConvRef = useRef(null);
  useEffect(() => {
    if (!convId || !messages.length) return undefined;
    const lastId = messages[messages.length - 1]?.id;
    // A jump-to-message (search result / old message) → centre + flash it, don't force bottom.
    if (jumpTargetRef.current) {
      const targetId = jumpTargetRef.current;
      jumpTargetRef.current = null;
      scrolledConvRef.current = convId;
      lastMsgIdRef.current = lastId;
      atBottomRef.current = false; setAtBottom(false);
      requestAnimationFrame(() => scrollToMessage(targetId));
      return undefined;
    }
    if (scrolledConvRef.current !== convId) {
      scrolledConvRef.current = convId;
      lastMsgIdRef.current = lastId;
      setNewCount(0); atBottomRef.current = true; setAtBottom(true);
      stickRef.current = Date.now() + 1200;
      const jump = () => bottomRef.current?.scrollIntoView({ block: "end" });
      jump();
      const timers = [60, 200, 450, 900].map((d) => setTimeout(jump, d));
      return () => timers.forEach(clearTimeout);
    }
    if (lastId && lastId !== lastMsgIdRef.current) {
      const last = messages[messages.length - 1];
      lastMsgIdRef.current = lastId;
      if (last?.sender_id === user?.id || atBottomRef.current) {
        stickRef.current = Date.now() + 4000;
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        setNewCount(0);
      } else {
        setNewCount((c) => c + 1);
      }
    }
    return undefined;
  }, [messages, convId, user?.id]);

  const isMobile = useIsMobile();
  // On mobile the chat is full-screen → Android back button closes it (back to inbox).
  useBackHandler(isMobile && !!chatTarget, () => { closeChat(); return true; });
  const { layerProps: dockLayerProps, sentinel: dockFocusSentinel } = useDockOverModal();

  // Mobile: the full-screen chat (z-80) would stay ON TOP of any page the user navigates
  // to (e.g. a notification tap → /post/… opened the "Qui a réagi" dialog over the chat).
  // Leaving the current page closes the chat; same-path query changes are ignored so
  // /messages?peer=… can still switch conversations.
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const prevPathRef = useRef(pathname);
  useEffect(() => {
    if (prevPathRef.current === pathname) return;
    prevPathRef.current = pathname;
    if (isMobile && chatTarget) closeChat();
  }, [pathname, isMobile, chatTarget, closeChat]);

  // Keep the full-screen mobile chat pinned to the VISIBLE viewport so the header
  // (avatar + name) stays on top when the keyboard opens — TikTok-style — regardless
  // of Android's adjustPan/adjustResize soft-input behaviour.
  const [visualVp, setVisualVp] = useState(null); // { h, top } from window.visualViewport
  useEffect(() => {
    if (!isMobile || !chatTarget) { setVisualVp(null); return; }
    const v = window.visualViewport;
    if (!v) return;
    const update = () => {
      const h = Math.round(v.height), top = Math.round(v.offsetTop);
      setVisualVp((cur) => (cur && cur.h === h && cur.top === top ? cur : { h, top }));  // lot 728 — pas de re-rendu si rien n'a bougé
      requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ block: "end" }));
    };
    update();
    v.addEventListener("resize", update);
    v.addEventListener("scroll", update);
    return () => { v.removeEventListener("resize", update); v.removeEventListener("scroll", update); };
  }, [isMobile, chatTarget]);

  // Keep freshly sent/received media pinned to the bottom AS IT LOADS. Images/videos
  // report their real height a moment after the bubble mounts, growing the stream and
  // pushing our new message below the fold. `load` (img) and `loadeddata`/
  // `loadedmetadata` (video) don't bubble, so we listen in the capture phase. We only
  // re-pin when the user is at the bottom or inside a short "stick" window, so we never
  // hijack someone reading older messages.
  useEffect(() => {
    const el = scrollContRef.current;
    if (!el || !chatTarget) return undefined;
    const repin = () => {
      if (Date.now() < stickRef.current || atBottomRef.current) {
        bottomRef.current?.scrollIntoView({ block: "end" });
      }
    };
    el.addEventListener("load", repin, true);
    el.addEventListener("loadeddata", repin, true);
    el.addEventListener("loadedmetadata", repin, true);
    return () => {
      el.removeEventListener("load", repin, true);
      el.removeEventListener("loadeddata", repin, true);
      el.removeEventListener("loadedmetadata", repin, true);
    };
  }, [chatTarget, convId]);

  const [blockOpen, setBlockOpen] = useState(false);
  if (!user) return null;
  if (!chatTarget) return null;

  // ── Multi-select (bulk delete / forward), WhatsApp-style ──
  const selectMode = selectedIds.size > 0;
  // Appui long (WhatsApp) : la barre d'outils du haut ET la barre d'emojis au-dessus de la bulle apparaissent ensemble.
  const enterSelect = (m) => { setSelectedIds(new Set([m.id])); setReactTarget(m.id); hapticLight(); };
  const toggleSelect = (m) => {
    setReactTarget(null);
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(m.id)) n.delete(m.id); else n.add(m.id);
      return n;
    });
  };
  const clearSel = () => { setSelectedIds(new Set()); setReactTarget(null); };
  const beginReply = (m) => composerRef.current?.startReply(m);
  const beginEdit = (m) => { clearSel(); setConfirmDelete(null); composerRef.current?.startEdit(m); };

  const doDelete = async (target, mode = "all") => {
    const list = Array.isArray(target) ? target : [target];
    setConfirmDelete(null); clearSel();
    const ids = new Set(list.map((x) => x.id));
    if (mode === "me") {
      // « Supprimer pour moi » : le message disparaît de MA vue seulement (l'autre le garde).
      setMessages((ms) => ms.filter((x) => !ids.has(x.id)));
      try { await api.post("/chat/messages/delete-for-me", { ids: [...ids] }); }
      catch (err) { toast.error(err?.response?.data?.detail || "Suppression impossible"); if (convId) loadMessages(convId); }
      return;
    }
    setMessages((ms) => ms.map((x) => (ids.has(x.id) ? { ...x, is_deleted: true, content: "", media_url: null, media_type: null, media_group: null } : x)));
    try { await Promise.all(list.map((x) => api.delete(`/chat/messages/${x.id}`))); }
    catch (err) { toast.error(err?.response?.data?.detail || "Suppression impossible"); if (convId) loadMessages(convId); }
  };

  // ⭐ Marquer / retirer un message important (mes messages ou ceux de l'autre).
  const toggleStar = async (m) => {
    clearSel();
    const next = !m.starred;
    setMessages((ms) => ms.map((x) => (x.id === m.id ? { ...x, starred: next } : x)));
    try {
      await api.post(`/chat/messages/${m.id}/star`);
      toast.success(next ? t("conv.starred_added", "Ajouté aux messages importants") : t("conv.starred_removed", "Retiré des messages importants"));
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); setMessages((ms) => ms.map((x) => (x.id === m.id ? { ...x, starred: !next } : x))); }
  };

  // Depuis la liste ⭐ : même conversation → centrer le message ; sinon ouvrir l'autre fil puis centrer.
  const pickStarred = (m) => {
    setStarredOpen(false);
    if (m.conversation_id === convId) { jumpToMessage(m); return; }
    if (m.peer) { pendingJumpRef.current = m; openChat(m.peer); }
  };

  // Forward one or several messages to another Yalola user/conversation ("Envoyer à").
  const openForward = (target) => {
    const list = Array.isArray(target) ? target : [target];
    clearSel();
    setForwardMsgs(list);
  };

  const scrollToMessage = (id) => {
    const el = document.querySelector(`[data-testid="chat-msg-${id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.style.transition = "box-shadow 0.3s ease";
    el.style.boxShadow = "0 0 0 3px var(--yalola-accent, #E0A63C)";
    setTimeout(() => { el.style.boxShadow = "none"; }, 1200);
  };

  const react = async (messageId, emoji) => {
    clearSel();
    const toggle = (ms) => ms.map((m) => {
      if (m.id !== messageId) return m;
      const r = { ...(m.reactions || {}) };
      if (r[user.id] === emoji) delete r[user.id]; else r[user.id] = emoji;
      return { ...m, reactions: r };
    });
    setMessages(toggle);
    try { await api.post(`/chat/messages/${messageId}/react`, { emoji }); }
    catch { setMessages(toggle); }  // lot 730 — refus serveur → on annule l'affichage optimiste (le toggle est sa propre inverse)
  };

  const doReportMessage = async (reason, category) => {
    if (!reportMsg || reporting) return;
    setReporting(true);
    try {
      await api.post(`/chat/messages/${reportMsg.id}/report`, { reason, category });
      toast.success(
        category === "child_safety"
          ? t("report.csae_toast", "Signalement urgent transmis à notre équipe de sécurité. Merci de protéger notre communauté.")
          : t("chat.reported_toast", "Merci, ce message a été signalé aux modérateurs")
      );
      if (category === "child_safety") setMessages((ms) => ms.filter((x) => x.id !== reportMsg.id));
      setReportMsg(null);
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setReporting(false); }
  };

  const lastSeenText = (isoStr) => {
    const d = new Date(isoStr); const now = new Date();
    const mins = Math.max(0, Math.floor((now - d) / 60000));
    if (mins < 1) return t("chat.seen_now", "Vu à l'instant");
    if (mins < 60) return t("chat.seen_min", "Vu il y a {n} min").replace("{n}", mins);
    const hrs = Math.floor(mins / 60);
    if (d.toDateString() === now.toDateString()) return t("chat.seen_hour", "Vu il y a {n} h").replace("{n}", hrs);
    return `${t("chat.seen", "Vu")} ${d.toLocaleDateString(localeFor(lang), { day: "numeric", month: "short" })}`;
  };
  const online = !!presence?.online;
  const statusText = peerTyping
    ? t("chat.typing", "écrit…")
    : online
      ? t("chat.active", "En ligne")
      : presence?.last_seen ? lastSeenText(presence.last_seen) : t("chat.offline", "Hors ligne");

  // Selection metadata (single vs bulk). Edit/reply/copy/report act on a single message.
  const selectedMsgs = messages.filter((mm) => selectedIds.has(mm.id) && !mm.is_deleted);
  const selCount = selectedMsgs.length;
  const single = selCount === 1 ? selectedMsgs[0] : null;
  const editWindowMin = Number(config?.settings?.chat?.edit_window_min) || 15;
  const withinWindow = (mm) => (Date.now() - new Date(mm.created_at).getTime()) < editWindowMin * 60 * 1000;
  const singleMine = single && single.sender_id === user.id;
  const singleText = single && (single.content || "").trim();
  const singleCanEdit = singleMine && withinWindow(single) && !!singleText && single.media_type !== "audio" && single.media_type !== "video";
  // « Supprimer pour moi » : n'importe quel message déjà sur le serveur ; « pour tout le monde » (mes messages,
  // sans limite de temps façon WhatsApp 2024+) est proposé dans la boîte de confirmation.
  const canDeleteAny = selCount > 0 && selectedMsgs.every((mm) => !mm.pending && !mm.failed);
  const copySel = () => { if (singleText) { copyToClipboard(single.content); toast.success(t("chat.copied", "Copié")); } clearSel(); };

  const openMedia = (mm, idx) => {
    const items = Array.isArray(mm.media_group) && mm.media_group.length
      ? mm.media_group.map((x) => ({ url: resolveMediaUrl(x.url), type: x.type === "video" ? "video" : "image" }))
      : [{ url: resolveMediaUrl(mm.media_url), type: mm.media_type === "video" ? "video" : "image" }];
    setLightbox({ items, index: idx || 0, messageId: mm.id });
  };

  const selectionToolbar = (
    <SelectionToolbar
      selCount={selCount}
      single={single}
      singleText={singleText}
      singleCanEdit={singleCanEdit}
      singleMine={singleMine}
      canDeleteAll={canDeleteAny}
      onClear={clearSel}
      onReply={(m) => { clearSel(); beginReply(m); }}
      onCopy={copySel}
      onForward={() => openForward(selectedMsgs)}
      onEdit={beginEdit}
      onDelete={() => setConfirmDelete(selectedMsgs)}
      onReport={(m) => { clearSel(); setReportMsg(m); }}
      onReactOpen={(m) => setReactTarget(m.id)}
      onStar={toggleStar}
      starLabel={single?.starred ? t("conv.unstar", "Retirer des importants") : t("conv.star", "Marquer important")}
      hint={t("chat.select_hint", "Touche d'autres messages pour les sélectionner")}
    />
  );

  const messagesList = (
    <MessageList
      messages={messages}
      user={user}
      lang={lang}
      t={t}
      chatTarget={chatTarget}
      peerTyping={peerTyping}
      peerLastRead={peerLastRead}
      peerLastDelivered={peerLastDelivered}
      reactTarget={reactTarget}
      bottomRef={bottomRef}
      onLongPress={enterSelect}
      onReply={beginReply}
      onReact={react}
      onReactOpen={(m) => setReactTarget((cur) => (cur === m.id ? null : m.id))}
      onReactionsOpen={(m) => setReactionsForId(m.id)}
      onSendBig={(emoji) => composerRef.current?.sendEmoji(emoji)}
      onQuickForward={openForward}
      onRetry={(m) => composerRef.current?.resend(m)}
      selectMode={selectMode}
      selectedIds={selectedIds}
      onToggleSelect={toggleSelect}
      onOpenMedia={openMedia}
      onQuoteClick={scrollToMessage}
    />
  );

  // WhatsApp-style floating "jump to bottom" pill: shows when the user has scrolled
  // up; a small badge appears when new messages arrived while they were reading up.
  const scrollPill = (!atBottom || newCount > 0 || hasMoreNewer) ? (
    <button
      onClick={jumpToBottom}
      className="absolute bottom-3 right-3 z-30 h-10 w-10 rounded-full grid place-items-center shadow-lg transition-transform active:scale-90 animate-in fade-in zoom-in"
      style={{ background: "var(--yalola-surface)", color: "var(--yalola-primary)", border: "1px solid var(--yalola-border)" }}
      data-testid="chat-scroll-bottom-btn"
      aria-label={t("chat.jump_to_bottom", "Aller au dernier message")}
    >
      <ChevronDown size={22} />
      {newCount > 0 && (
        <span
          className="absolute -top-1.5 -right-1.5 h-5 min-w-[20px] px-1 rounded-full text-[11px] font-bold grid place-items-center text-white"
          style={{ background: "var(--yalola-primary)" }}
          data-testid="chat-scroll-bottom-count"
        >{newCount}</span>
      )}
    </button>
  ) : null;

  // Lot 636 : compte protégé (super admin / « Yalola App ») → pas de bouton « Bloquer » dans le chat.
  const blockButton = chatTarget?.is_protected ? null : (
    <button
      onClick={(e) => { e.stopPropagation(); setBlockOpen(true); }}
      className="h-9 w-9 grid place-items-center rounded-full hover:bg-red-50 shrink-0 text-red-600"
      data-testid="chat-block-btn"
      aria-label={t("block.confirm", "Bloquer")}
      title={t("block.action", "Bloquer {name}").replace("{name}", chatTarget?.name || "")}
    >
      <Ban size={18} />
    </button>
  );
  const blockDialogEl = (
    <BlockUserDialog open={blockOpen} onOpenChange={setBlockOpen} user={chatTarget} testidPrefix="chat-block" onBlocked={() => closeChat()} />
  );
  const searchButton = (
    <button
      onClick={(e) => { e.stopPropagation(); setSearchOpen((v) => { const nv = !v; if (!nv) { setSearchQ(""); setSearchResults([]); } return nv; }); }}
      className="h-9 w-9 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)] shrink-0"
      style={{ color: searchOpen ? "var(--yalola-primary)" : "var(--yalola-text-muted)" }}
      data-testid="chat-search-btn"
      aria-label={t("chat.search", "Rechercher dans la conversation")}
    >
      <Search size={19} />
    </button>
  );

  const searchBar = searchOpen ? (
    <div className="border-b bg-white px-2 py-2 shrink-0" style={{ borderColor: "var(--yalola-border)" }} data-testid="chat-search-bar">
      <div className="flex items-center gap-2">
        <Search size={16} style={{ color: "var(--yalola-text-muted)" }} />
        <input
          autoFocus
          value={searchQ}
          onChange={(e) => runSearch(e.target.value)}
          placeholder={t("chat.search_ph", "Rechercher un message…")}
          className="flex-1 text-sm outline-none bg-transparent"
          style={{ color: "var(--yalola-text)" }}
          data-testid="chat-search-input"
        />
        <button onClick={() => { setSearchOpen(false); setSearchQ(""); setSearchResults([]); }} className="p-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="chat-search-close"><X size={16} /></button>
      </div>
      {searchQ.trim().length >= 2 && (
        <div className="mt-2 max-h-56 overflow-y-auto rounded-lg" data-testid="chat-search-results">
          {searching ? (
            <div className="text-xs px-2 py-3 text-center" style={{ color: "var(--yalola-text-muted)" }}>{t("chat.searching", "Recherche…")}</div>
          ) : searchResults.length === 0 ? (
            <div className="text-xs px-2 py-3 text-center" style={{ color: "var(--yalola-text-muted)" }}>{t("chat.no_results", "Aucun résultat")}</div>
          ) : (
            searchResults.map((rmsg) => (
              <button
                key={rmsg.id}
                onClick={() => jumpToMessage(rmsg)}
                className="w-full text-left px-2 py-2 rounded-lg hover:bg-[color:var(--yalola-surface-alt)] flex items-center justify-between gap-2"
                data-testid={`chat-search-result-${rmsg.id}`}
              >
                <span className="text-xs truncate flex-1" style={{ color: "var(--yalola-text)" }}>{rmsg.content || t("chat.media_message", "Média")}</span>
                <span className="text-[10px] shrink-0" style={{ color: "var(--yalola-text-muted)" }}>{new Date(rmsg.created_at).toLocaleDateString()}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  ) : null;

  const composerEl = (
    <Composer
      ref={composerRef}
      user={user}
      chatTarget={chatTarget}
      convId={convId}
      setConvId={setConvId}
      setMessages={setMessages}
      outbox={outbox}
      config={config}
      t={t}
      isMobile={isMobile}
      wsRef={wsRef}
    />
  );

  // Demande de message : bandeau Accepter / Supprimer pour le destinataire ; rappel discret pour l'expéditeur.
  const actOnRequest = async (action) => {
    if (!convId) return;
    try {
      await api.post(`/chat/conversations/${convId}/request`, { action });
      window.dispatchEvent(new CustomEvent("yalola:conv"));
      if (action === "accept") { setConvFlags((f) => ({ ...(f || {}), is_request: false, request_pending: false })); toast.success(t("messages.request_accepted", "Demande acceptée")); }
      else { toast.success(t("messages.request_deleted", "Demande supprimée")); closeChat(); }
    } catch (err) { toast.error(err?.friendlyMessage || err?.response?.data?.detail || "Erreur"); }
  };
  const requestBanner = convFlags?.is_request ? (
    <div className="shrink-0 border-t px-3 py-2 flex items-center gap-2" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-primary-soft, #fff7ed)" }} data-testid="chat-request-banner">
      <div className="flex-1 min-w-0">
        <p className="text-xs" style={{ color: "var(--yalola-text)" }}>
          {t("chat.request_banner", "{name} souhaite t'envoyer des messages. Répondre revient à accepter.").replace("{name}", chatTarget.name || "")}
        </p>
        <button onClick={() => navigate(`/profile/${chatTarget.id}`)} className="text-xs font-semibold underline underline-offset-2 mt-0.5" style={{ color: "var(--yalola-primary)" }} data-testid="chat-request-profile">{t("matching.view_profile", "Voir le profil")}</button>
      </div>
      <button onClick={() => actOnRequest("accept")} className="h-8 px-3 rounded-full text-xs font-semibold text-white shrink-0" style={{ background: "var(--yalola-primary)" }} data-testid="chat-request-accept">{t("messages.request_accept", "Accepter")}</button>
      <button onClick={() => actOnRequest("delete")} className="h-8 px-3 rounded-full text-xs font-semibold text-red-600 shrink-0" style={{ background: "var(--yalola-surface)", border: "1px solid var(--yalola-border)" }} data-testid="chat-request-delete">{t("messages.request_delete", "Supprimer")}</button>
    </div>
  ) : (convFlags?.request_pending && !convFlags?.is_request) ? (
    <div className="shrink-0 border-t px-3 py-1.5 text-[11px] text-center" style={{ borderColor: "var(--yalola-border)", color: "var(--yalola-text-muted)", background: "var(--yalola-surface-alt)" }} data-testid="chat-request-sent">
      {t("chat.request_sent", "Demande envoyée · {name} la verra dans ses demandes de messages").replace("{name}", chatTarget.name || "")}
    </div>
  ) : null;

  const lightboxEl = (
    <ChatLightbox
      lightbox={lightbox}
      t={t}
      onClose={() => setLightbox(null)}
      onForward={(messageId) => { const mm = messages.find((x) => x.id === messageId); setLightbox(null); if (mm) openForward(mm); }}
      onReact={react}
    />
  );

  const forwardSheetEl = (
    <ForwardSheet
      messages={forwardMsgs}
      convId={convId}
      isMobile={isMobile}
      t={t}
      onClose={() => setForwardMsgs([])}
      onForwarded={(msg) => setMessages((ms) => [...ms, msg])}
    />
  );

  const reportDialogEl = (
    <ReportDialog
      open={!!reportMsg}
      onOpenChange={(v) => { if (!v) setReportMsg(null); }}
      onReport={doReportMessage}
      reporting={reporting}
      testidPrefix="chat-report"
      title={t("chat.report_title", "Signaler ce message")}
    />
  );

  const deleteCount = Array.isArray(confirmDelete) ? confirmDelete.length : (confirmDelete ? 1 : 0);
  const deleteAllMine = deleteCount > 0 && (Array.isArray(confirmDelete) ? confirmDelete : [confirmDelete]).every((mm) => mm.sender_id === user.id);
  const deleteConfirmEl = deleteCount > 0 ? (
    <div className="fixed inset-0 z-[96] flex items-center justify-center bg-black/50 p-4" data-testid="chat-delete-confirm" onClick={() => setConfirmDelete(null)}>
      <div className="bg-white rounded-2xl p-5 w-full max-w-xs shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="font-bold text-sm mb-1" style={{ color: "var(--yalola-text)" }}>
          {deleteCount > 1
            ? t("chat.delete_title_n", "Supprimer {n} messages ?").replace("{n}", deleteCount)
            : t("chat.delete_title", "Supprimer ce message ?")}
        </div>
        <p className="text-xs mb-4" style={{ color: "var(--yalola-text-muted)" }} data-testid="chat-delete-desc">
          {deleteAllMine
            ? t("chat.delete_choose_desc", "« Pour moi » retire le message de ton côté seulement. « Pour tout le monde » le remplace par « Ce message a été supprimé » chez les deux, quelle que soit sa date.")
            : t("chat.delete_for_me_desc", "Le message disparaît seulement de ton côté ; {name} le garde.").replace("{name}", chatTarget.name || "")}
        </p>
        <div className="flex flex-col gap-2">
          {deleteAllMine && (
            <button onClick={() => doDelete(confirmDelete, "all")} className="w-full px-3.5 py-2 rounded-full text-sm font-semibold text-white" style={{ background: "#e11d48" }} data-testid="chat-delete-confirm-btn">{t("chat.delete_for_all", "Supprimer pour tout le monde")}</button>
          )}
          <button onClick={() => doDelete(confirmDelete, "me")} className="w-full px-3.5 py-2 rounded-full text-sm font-semibold" style={{ background: deleteAllMine ? "var(--yalola-surface-alt)" : "#e11d48", color: deleteAllMine ? "#e11d48" : "#fff" }} data-testid="chat-delete-for-me-btn">{t("chat.delete_for_me", "Supprimer pour moi")}</button>
          <button onClick={() => setConfirmDelete(null)} className="w-full px-3.5 py-2 rounded-full text-sm font-medium" style={{ color: "var(--yalola-text-muted)" }} data-testid="chat-delete-cancel">{t("common.cancel", "Annuler")}</button>
        </div>
      </div>
    </div>
  ) : null;

  // Menu ⋮ de la conversation (épingler · sourdine · archiver · non lu · supprimer · ⭐ importants).
  const menuButton = convId ? (
    <button
      onClick={(e) => { e.stopPropagation(); setMenuOpen(true); }}
      className="h-9 w-9 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)] shrink-0"
      style={{ color: "var(--yalola-text-muted)" }}
      data-testid="chat-menu-btn"
      aria-label={t("conv.more", "Plus d'options")}
    >
      <MoreVertical size={19} />
    </button>
  ) : null;
  const menuSheetEl = (
    <ConvActionsSheet
      conv={menuOpen && convId ? { id: convId, other: chatTarget, ...(convFlags || {}) } : null}
      t={t}
      isMobile={isMobile}
      onClose={() => setMenuOpen(false)}
      onChanged={(flags) => { setConvFlags((f) => ({ ...(f || {}), ...flags })); window.dispatchEvent(new CustomEvent("yalola:conv")); }}
      onDeleted={() => { window.dispatchEvent(new CustomEvent("yalola:conv")); closeChat(); }}
      onStarred={() => setStarredOpen(true)}
      onViewProfile={() => navigate(`/profile/${chatTarget.id}`)}
    />
  );
  const starredSheetEl = (
    <StarredSheet open={starredOpen} t={t} lang={lang} isMobile={isMobile} onClose={() => setStarredOpen(false)} onPick={pickStarred} />
  );
  const reactionsSheetEl = (
    <ReactionsSheet
      message={reactionsForId ? messages.find((mm) => mm.id === reactionsForId) || null : null}
      user={user}
      peer={chatTarget}
      t={t}
      isMobile={isMobile}
      onClose={() => setReactionsForId(null)}
      onRemoveMine={(id, emoji) => { react(id, emoji); setReactionsForId(null); }}
    />
  );

  // ── Mobile / tablet: full-screen integrated chat page (TikTok-style) ──
  if (isMobile) {
    return (
      <div
        className="fixed left-0 right-0 top-0 z-[80] flex flex-col"
        style={{
          background: "var(--yalola-surface)",
          height: visualVp ? `${visualVp.h}px` : "100dvh",
          transform: visualVp ? `translateY(${visualVp.top}px)` : undefined,
          ...dockLayerStyle,
        }}
        data-testid="chat-dock"
        {...dockLayerProps}
      >
        {dockFocusSentinel}
        <div
          className="flex items-center gap-2 px-2 pb-2.5 shrink-0 border-b bg-white"
          style={{ borderColor: "var(--yalola-border)", paddingTop: "calc(0.625rem + var(--sa-top))" }}
          data-testid="chat-fullscreen-header"
        >
          <button onClick={closeChat} className="h-9 w-9 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)] shrink-0" style={{ color: "var(--yalola-primary)" }} data-testid="chat-back-btn"><ArrowLeft size={22} /></button>
          <div className="relative shrink-0">
            <Avatar className="h-9 w-9">
              <AvatarImage src={avatarSrc(chatTarget)} />
              <AvatarFallback>{chatTarget.name?.[0]}</AvatarFallback>
            </Avatar>
            <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white" style={{ background: online ? "#22c55e" : "#9ca3af" }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold truncate flex items-center gap-1" style={{ color: "var(--yalola-primary)" }}>{chatTarget.name} <VerifiedBadge user={chatTarget} size={13} /></div>
            <div className="text-[11px] truncate" style={{ color: "var(--yalola-text-muted)" }} data-testid="chat-peer-status">{statusText}</div>
          </div>
          {searchButton}
          {menuButton}
          {blockButton}
        </div>
        {selectionToolbar}
        {searchBar}
        <div className="relative flex-1 min-h-0 flex flex-col">
          <div ref={scrollContRef} onScroll={onMessagesScroll} className="flex-1 overflow-y-auto p-3 space-y-1.5 yalola-chat-bg" data-testid="chat-dock-messages">
            {messagesList}
          </div>
          {scrollPill}
        </div>
        {requestBanner}
        {composerEl}
        {lightboxEl}
        {reportDialogEl}
        {blockDialogEl}
        {deleteConfirmEl}
        {forwardSheetEl}
        {menuSheetEl}
        {starredSheetEl}
        {reactionsSheetEl}
      </div>
    );
  }

  // ── Desktop: floating Facebook-style dock (bottom-right) ──
  return (
    <div
      className="fixed chat-dock-offset right-3 md:right-6 z-[80] w-[300px] sm:w-[330px] rounded-t-2xl shadow-2xl border border-b-0 flex flex-col bg-white overflow-hidden"
      style={{ borderColor: "var(--yalola-border)", ...dockLayerStyle }}
      data-testid="chat-dock"
      {...dockLayerProps}
    >
      {dockFocusSentinel}
      <div
        className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none border-b bg-white"
        style={{ borderColor: "var(--yalola-border)" }}
        onClick={() => setMinimized(!minimized)}
        data-testid="chat-dock-header"
      >
        <div className="relative shrink-0">
          <Avatar className="h-8 w-8">
            <AvatarImage src={avatarSrc(chatTarget)} />
            <AvatarFallback>{chatTarget.name?.[0]}</AvatarFallback>
          </Avatar>
          <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white" style={{ background: online ? "#22c55e" : "#9ca3af" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold truncate flex items-center gap-1" style={{ color: "var(--yalola-primary)" }}>{chatTarget.name} <VerifiedBadge user={chatTarget} size={12} /></div>
          <div className="text-[10px] truncate" style={{ color: "var(--yalola-text-muted)" }} data-testid="chat-peer-status">{statusText}</div>
        </div>
        {searchButton}
        {menuButton}
        {blockButton}
        <button onClick={(e) => { e.stopPropagation(); setMinimized(!minimized); }} className="p-1 hover:opacity-70" style={{ color: "var(--yalola-text-muted)" }} data-testid="chat-dock-minimize"><Minus size={16} /></button>
        <button onClick={(e) => { e.stopPropagation(); closeChat(); }} className="p-1 hover:opacity-70" style={{ color: "var(--yalola-text-muted)" }} data-testid="chat-dock-close"><X size={16} /></button>
      </div>

      <div className={minimized ? "hidden" : "contents"}>
        {selectionToolbar}
        {searchBar}
        <div className="relative" style={{ height: 280 }}>
          <div ref={scrollContRef} onScroll={onMessagesScroll} className="h-full overflow-y-auto p-3 space-y-1.5 yalola-chat-bg" data-testid="chat-dock-messages">
            {messagesList}
          </div>
          {scrollPill}
        </div>
        {requestBanner}
        {composerEl}
      </div>
      {lightboxEl}
      {reportDialogEl}
      {blockDialogEl}
      {deleteConfirmEl}
      {forwardSheetEl}
      {menuSheetEl}
      {starredSheetEl}
      {reactionsSheetEl}
    </div>
  );
};

export default ChatDock;
